import { WireShapePaint } from './types/wire-shape-paint'

/**
 * Kinds whose builder writes `fillStyle` CONDITIONALLY on the shape's own fill
 * (`buildShapeFillStyle` in `stowed-element-builder.ts`), which is what makes both presence AND
 * absence meaningful.
 *
 * Deliberately excluded:
 * - kind 9 (Polyline) has no `fill_style` field in the proto at all.
 *
 * Kind 8 (Oval) and kind 10 (RegularPolygon) are now included:
 * - kind 10 previously wrote `fillStyle` UNCONDITIONALLY (`buildRegularPolygonFillStyle`), so all
 *   saved regular polygons carry it; switching to `buildShapeFillStyle` means absence is now
 *   unambiguously None fill.
 * - kind 8 now writes `fillStyle` through `buildShapeFillStyle`, so absence signals None fill.
 *   Ovals saved before that builder change carry no `fillStyle` and will therefore reload as
 *   transparent — an accepted trade-off to make None fill persistent on this kind.
 *
 * This set is defined as "the callers of `buildShapeFillStyle`", so the reader is derived from the
 * writer and cannot silently drift from it.
 */
const FILL_EVIDENCE_KIND_IDS = new Set([1, 2, 8, 10])

/** Kinds whose builder writes `lineStyle` (`buildLineStyle`) and whose proto has `line_style`.
 *  Kind 10 is excluded: `RegularPolygonElement` has no `line_style` field. */
const LINE_EVIDENCE_KIND_IDS = new Set([1, 2, 8, 9])

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value !== '') {
    const n = Number(value)

    return Number.isFinite(n) ? n : null
  }

  return null
}

/**
 * Reads the paint evidence off a decoded element root.
 *
 * `root` must be the ELEMENT root (post-`unwrapElementData`), never a deep search: `TextRun` also
 * carries `lineStyle`/`fillStyle` (proto fields 12/13), and in real NSD every `<FS>`/`<LS>` in a
 * text-heavy spread lives inside a `<TR>`. Picking one of those up would report a text colour as
 * the shape's fill.
 */
export function readWireShapePaint(root: Record<string, unknown>, kindId: number): WireShapePaint {
  const fillStyle = asRecord(root.fillStyle ?? root.FillStyle ?? root.fill_style)
  const lineStyle = asRecord(root.lineStyle ?? root.LineStyle ?? root.line_style)

  // Presence of the key is the WHOLE fill test. Do NOT strengthen this to `colorId > 0`: colour id
  // 0 is a real palette colour, not "no colour" — in the production fixture
  // `larry-load-api/src/fixtures/e71cbb13-….xml`, 11 of 19 `<FS>` elements and 20 of 21 `<LS>` elements use
  // `CI="0"`. Tightening the test would strip the paint from exactly those shapes.
  const hasVisibleFill = FILL_EVIDENCE_KIND_IDS.has(kindId) ? fillStyle != null : null

  const strokeWeight = lineStyle ? finiteNumber(lineStyle.weight ?? lineStyle.Weight) : null

  let hasVisibleStroke: boolean | null = null

  if (LINE_EVIDENCE_KIND_IDS.has(kindId)) {
    if (!lineStyle) {
      // yb360 omits `<LS>` entirely for an unstroked shape, and larry always writes one.
      hasVisibleStroke = false
    } else {
      // `weight` alone proves nothing — `buildLineStyle` always writes it, including `weight: 0`
      // for a transparent text frame — so the colour half has to carry the signal too. But the
      // converse also holds: legacy `<LS WT="1" CI="0"/>` is a visible stroke whose `colorId` is
      // dropped by proto3 default-omission. Accepting either separates a real stroke from a text
      // frame's `{ weight: 0 }` on both transports.
      const colorId = finiteNumber(lineStyle.colorId ?? lineStyle.ColorId ?? lineStyle.color_id)

      hasVisibleStroke = (strokeWeight ?? 0) > 0 || (colorId ?? 0) > 0
    }
  }

  const fillColorId = fillStyle
    ? finiteNumber(fillStyle.colorId ?? fillStyle.ColorId ?? fillStyle.color_id)
    : null
  const fillTint = fillStyle ? finiteNumber(fillStyle.tint ?? fillStyle.Tint) : null

  return { hasVisibleFill, hasVisibleStroke, strokeWeight, fillColorId, fillTint }
}
